import { createClient, SupabaseClient } from '@supabase/supabase-js'
import { withSchemaErrorGuard, reportSchemaError } from '@/lib/supabaseSchemaGuard'
import { createBoundedAuthLock } from '@/lib/supabaseAuthLock'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error("Supabase URL and Anon Key must be provided in environment variables.");
}

// Use a more robust singleton pattern with window-based storage
const GLOBAL_KEY = '__JOULES_SUPABASE_INSTANCE__';

/**
 * Where supabase persists the session, and the name behind its auth Web Lock
 * (`lock:<storageKey>`). Exported because sessionBoot.ts reads the persisted session
 * directly when `getSession()` is too slow to answer, and a second copy of this string
 * would silently stop matching the day it changed.
 */
export const SUPABASE_AUTH_STORAGE_KEY = 'solerro-pm-auth';

function getSupabaseClient(): SupabaseClient {
  // Check for an existing instance in the global scope
  if (typeof window !== 'undefined') {
    const existingClient = (window as any)[GLOBAL_KEY];
    if (existingClient) {
      return existingClient;
    }
  }

  // Create new instance with production-optimized settings
  const client = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      storageKey: SUPABASE_AUTH_STORAGE_KEY,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
      storage: typeof window !== 'undefined' ? window.localStorage : undefined,
      /**
       * ⚠️ A bounded auth lock, because the default one can wedge the whole app.
       *
       * auth-js wraps auth operations in a Web Lock named `lock:<storageKey>` and calls
       * `_acquireLock(-1, ...)` — wait forever — for `getSession()`. If a request inside
       * `_initialize()` is aborted, which happens routinely when the app navigates while
       * auth is in flight, the lock is left held and every later `getSession()` blocks
       * indefinitely. `App.tsx` awaits `getSession()` in `checkUserStatus`, so its
       * `finally { setIsLoading(false) }` never runs and the app sits on "Loading..."
       * until the tab is reloaded.
       *
       * Measured before this change, after signing in and navigating straight away —
       * 2 runs in 3, with the third recovering only after queueing 150+ lock requests:
       *     LOCKS: held [{ name: "lock:solerro-pm-auth", mode: "exclusive" }]
       *     acquire same lock -> BLOCKED (lock held)
       *     in-page getSession(): NEVER SETTLED
       * and after it, 5 runs in 5:
       *     LOCKS: held=0 pending=0
       *     acquire same lock -> ACQUIRED
       *     in-page getSession(): SESSION in 0ms
       *
       * ⚠️ This bounds a CONTENDED lock, not a permanently held one — a lock that is never
       * released still leaves the app on "Loading...". See supabaseAuthLock.ts, which
       * records both measurements and where the missing guarantee actually belongs.
       */
      lock: createBoundedAuthLock({
        onTimeout: (name, timeoutMs) => {
          console.warn(
            `[supabase] auth lock "${name}" was not released within ${timeoutMs}ms; ` +
              'proceeding without it so the app cannot hang. See supabaseAuthLock.ts.'
          );
        }
      })
    },
    global: {
      headers: {
        'x-application-name': 'joules-pm'
      },
      // Surfaces PostgREST schema errors (42P01/42703/42883) instead of letting them
      // degrade to an empty state in whichever caller happens to catch them. See
      // supabaseSchemaGuard.ts and docs/fixes/lead-map-orphaned-by-pins-rename.md —
      // the Lead Map 404d on every query for months and looked like an empty map.
      // Pass-through for every other outcome, including RLS denials.
      fetch: withSchemaErrorGuard(
        (input, init) => fetch(input, init),
        reportSchemaError
      )
    },
    // Add cache settings for better production performance
    db: {
      schema: 'public'
    },
    realtime: {
      params: {
        eventsPerSecond: 2
      }
    }
  });

  // Store in global scope
  if (typeof window !== 'undefined') {
    (window as any)[GLOBAL_KEY] = client;

    // Add error handling for token expiration
    client.auth.onAuthStateChange((event, session) => {
      if (event === 'TOKEN_REFRESHED') {
      } else if (event === 'SIGNED_OUT') {
        // Only store the return URL if we're not already on the login page
        const currentPath = window.location.pathname + window.location.search;
        if (currentPath !== '/login') {
          localStorage.setItem('returnTo', currentPath);
        }
        // Dispatch custom event for React Router navigation instead of hard refresh
        window.dispatchEvent(new CustomEvent('auth-signed-out'));
      }
    });
  }

  return client;
}

// Export the singleton instance
export const supabase = getSupabaseClient();
